import { Inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { NgxSpinnerService } from 'ngx-spinner';
import {
  ConsumptionsFormGroup,
  countTypeKeywords,
  DataComparisonFormGroup,
  ElectricityEquipmentFormGroup,
  EnergyMeter,
  EnergyType,
  EntityFormGroup,
  EnumItem,
  EnumsDictionary,
  InvoiceDataFormGroup,
  InvoiceProcessorResult,
  NaturalGasEquipmentFormGroup,
  pressureLevelAliases,
  tariffOptionKeywords,
  tensionLevelAliases,
  tierKeywords,
  timeCycleKeywords,
} from './invoice-processor-types';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormControlOptions,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '../services/generic.service';

import { endOfMonth, startOfMonth, subMonths } from 'date-fns';
import { InvoiceProcessorDataService } from './invoice-processor-data.service';
import {
  ElectricityMeterConsumption,
  NaturalGasMeterConsumption,
} from '../models/EnergyConsumptions';
import { RegExpPatterns } from '../helpers/reg-exp-patterns';
import { Supplier } from '../models/Supplier';
import { ENVIRONMENT, Environment } from '../environments/environment.token';
import { catchError, distinctUntilChanged, finalize, firstValueFrom, forkJoin, from, map, Observable, of, shareReplay, Subscription, switchMap, tap, throwError } from 'rxjs';
/* -------------------------------------------------------------------------- */
/*                             SERVIÇO PRINCIPAL                              */
/* -------------------------------------------------------------------------- */

@Injectable({ providedIn: 'root' })
export class InvoiceProcessorService {
  private readonly MAX_SIZE = 10 * 1024 * 1024;
  private readonly countryCode: string;
  private subscriptions: Subscription[] = [];

  private enums$!: Observable<EnumsDictionary>;
  private enums!: EnumsDictionary;
  private apiUrl: string;

  constructor(
    private readonly generic: GenericService,
    private readonly _invoiceProcessorData: InvoiceProcessorDataService,
    private _spinner: NgxSpinnerService,
    private _fb: FormBuilder,
    private _translateService: TranslateService,
    @Inject(ENVIRONMENT) private env: Environment
  ) {
    this.apiUrl = this.env.apiUrl;
    this.countryCode = localStorage.getItem('selectedSIGA') ?? 'pt';
    this.enums$ = this.loadEnums().pipe(shareReplay(1));
    this.enums$.subscribe((x) => (this.enums = x));
  }

  /* ------------------------------- PUBLIC -------------------------------- */
  public getMeterByFile(
    file: File,
    callLoader: boolean = true, apiUrl: string
  ): Observable<InvoiceProcessorResult> {

    this.apiUrl = apiUrl;

    if (file.type !== 'application/pdf') {
      //this._snackbar.callSnackBar('PDF apenas.', SnackBarType.WARNING);
      return throwError(() => new Error('PDF apenas.'));
    }

    if (file.size > this.MAX_SIZE) {
      //this._snackbar.callSnackBar('Ficheiro >10 MB.', SnackBarType.WARNING);
      return throwError(() => new Error('Ficheiro >10 MB.'));
    }

    if (callLoader) this._spinner.show();

    return this.uploadToAzure(file).pipe(
      switchMap((resp) => from(this.mapAzureResponse(resp, file))),
      switchMap((p) => {
        const tin = p?.customerTin.value?.trim?.();
        const hasValidTin = tin && !isNaN(Number(tin));

        const entity$ = hasValidTin
          ? from(this.getEntity(Number(tin), p)).pipe(
            catchError((err) => {
              //this._snackbar.callSnackBar(
              //  'Entidade não encontrada.',
              //  SnackBarType.WARNING
              //);
              return of(null); // proceed without entity
            })
          )
          : of(null);

        const equipments$ = this.resolveIds(p).pipe(
          switchMap((resolved) => this.buildEquipments(null, resolved)),
          catchError((err) => {
            //this._snackbar.callSnackBar(
            //  'Equipamentos não encontrados.',
            //  SnackBarType.WARNING
            //);
            return of([]); // proceed with empty list
          })
        );

        return forkJoin({
          invoice: of(new FormGroup<InvoiceDataFormGroup>(p)),
          entity: entity$,
          equipments: equipments$,
        });
      }),
      map(({ invoice, entity, equipments }) => ({
        invoice,
        entity,
        equipments,
      })),
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          //this._snackbar.callSnackBar(
          //  'Erro ao processar a fatura.',
          //  SnackBarType.ERROR
          //);
        }
        return throwError(() => err);
      }),
      finalize(() => {
        if (callLoader) this._spinner.hide();
      })
    );
  }

  public getMeterByCode(
    meterCode: string,
    asFormGroup: boolean = true,
    apiUrl: string
  ): Observable<EnergyMeter[] | any[]> {
    this._spinner.show();
    this.apiUrl = apiUrl;

    return this.buildEquipments(meterCode, null).pipe(
      map((equipments) => {
        return asFormGroup
          ? equipments
          : equipments.map((e) => this.deepUnwrapForm(e));
      }),
      tap(() => this._spinner.hide())
    );
  }

  /* ------------------------------ Upload --------------------------------- */
  private uploadToAzure(file: File): Observable<any> {
    const fd = new FormData();
    fd.append('file', file, file.name);

    const modelId = 'di:InvoiceExtractDataPT';

    return this.generic.post(
      `${this.apiUrl}/DocumentIntelligence/AnalyzeDocument?modelId=${encodeURIComponent(modelId)}`,
      fd
    );
  }

  /* ------------------------------ ENUMS ---------------------------------- */
  private loadEnums(): Observable<EnumsDictionary> {
    const E = `${this.apiUrl}/Electricity/${this.countryCode}/`;
    const G = `${this.apiUrl}/NaturalGas/${this.countryCode}/`;
    return forkJoin({
      tensionLevels: this.fetchEnum(`${E}TensionLevels`),
      timeCycles: this.fetchEnum(`${E}TimeCycles`),
      countTypes: this.fetchEnum(`${E}CountingCycles`),
      pressureLevels: this.fetchEnum(`${G}PressureLevels`),
      tariffOptions: this.fetchEnum(`${G}TariffOptions`),
      tiers: this.fetchEnum(`${G}Tiers`),
    });
  }

  /* ------------------------------ ENTITY ---------------------------------- */
  private async getEntity(
    tin: number,
    invoice: InvoiceDataFormGroup
  ): Promise<FormGroup<EntityFormGroup>> {
    const entityExistence = await firstValueFrom(
      this._invoiceProcessorData.getEntityByTin(tin.toString(), true, this.apiUrl)
    );

    if (entityExistence.data.exists) {
      const srEntity = await firstValueFrom(
        this._invoiceProcessorData.getEntity(entityExistence.data.id, true, this.apiUrl)
      );

      const entity = srEntity?.data;

      return this._fb.group<EntityFormGroup>({
        id: new FormControl<string>(entity.id),
        name: new FormControl<string>(entity.name),
        tin: new FormControl<number>(entity.tin),
        email: this.buildGroup<string | null>(
          invoice.customerEmail.value,
          entity.email,
          null,
          null
        ),
        phoneNumber: this.buildGroup<string | null>(
          invoice.customerPhoneNumber.value,
          entity.phoneNumber,
          null,
          null
        ),
        address: this.buildGroup<string | null>(
          invoice.customerAddress.value,
          entity.address?.street ?? null,
          null,
          null
        ),
        postalCode: this.buildGroup<string | number | null>(
          invoice.customerPostalCode.value,
          entity.address?.postalCode ?? null,
          null,
          entity.address?.postalCodeId
        ),
        parish: this.buildGroup<string | number | null>(
          invoice.customerParish.value,
          entity.address?.parish ?? null,
          null,
          entity.address?.parishId
        ),
      });
    }

    throw new Error('Entidade não encontrada.');
  }

  private fetchEnum(url: string): Observable<Map<string, string>> {
    return this.generic.get<EnumItem[]>(url).pipe(
      map((res) => {
        const m = new Map<string, string>();
        res.data.forEach((i) => m.set(i.description.toLowerCase(), i.id));
        return m;
      }),
      catchError(() => of(new Map<string, string>()))
    );
  }

  /* ------------------- EQUIPMENTS (METERS + CONSUMPTIONS) ---------------- */
  private buildEquipments(
    meterCode: string | null,
    form: InvoiceDataFormGroup | null
  ): Observable<EnergyMeter[]> {
    const endDate = subMonths(endOfMonth(Date.now()), 2);
    const startDate = subMonths(startOfMonth(endDate), 11);
    const tasks: Observable<EnergyMeter | null>[] = [];

    const addTask = (
      type: 'electricity' | 'gas',
      code: string,
      formContext: InvoiceDataFormGroup | null = null
    ) => {
      tasks.push(
        this.getMeterWithConsumptions(type, code, startDate, endDate).pipe(
          map(({ meter, consumptions }) =>
            this.compose(type, formContext, meter, consumptions)
          )
        )
      );
    };

    if (meterCode) {
      const isElectricity = this._meterCodeRegexpPattern(true).test(meterCode);
      const isGas = this._meterCodeRegexpPattern(false).test(meterCode);

      if (isElectricity) addTask('electricity', meterCode);
      else if (isGas) addTask('gas', meterCode);
    } else if (form) {
      const electricityCode = form.electricityEquipmentCode?.value;
      const gasCode = form.naturalGasEquipmentCode?.value;

      if (electricityCode) addTask('electricity', electricityCode, form);
      if (gasCode) addTask('gas', gasCode, form);
    }

    return tasks.length
      ? forkJoin(tasks).pipe(
        map((results) => results.filter((x): x is EnergyMeter => x !== null))
      )
      : of([]);
  }

  private getMeterWithConsumptions(
    type: EnergyType,
    code: string,
    dFrom: Date,
    dTo: Date
  ): Observable<{
    meter: any | null;
    consumptions: FormArray<FormGroup<ConsumptionsFormGroup>>;
  }> {
    const from = dFrom.toISOString().split('T')[0];
    const to = dTo.toISOString().split('T')[0];

    const buildElectricityConsumptionFormArray = (
      data: ElectricityMeterConsumption[] | null
    ): FormArray<FormGroup<ConsumptionsFormGroup>> => {
      const formGroups = (data ?? []).map((consumption) => {
        const fg = this._fb.group<ConsumptionsFormGroup>({
          date: this._fb.control(consumption.date, { nonNullable: true }),
          offPeak: this._fb.control(consumption.offPeak, {
            nonNullable: true,
            validators: Validators.required,
          }),
          peak: this._fb.control(consumption.peak, {
            nonNullable: true,
            validators: Validators.required,
          }),
          standard: this._fb.control(consumption.standard, {
            nonNullable: true,
            validators: Validators.required,
          }),
          superOffPeak: this._fb.control(consumption.superOffPeak ?? 0, {
            nonNullable: true,
            validators: Validators.required,
          }),
          gas: this._fb.control(0, { nonNullable: true }), // valor zerado para não ser usado
          id: this._fb.control(consumption.id, { nonNullable: true }),
          singleLine: this._fb.control(false, { nonNullable: true }),
        });
        fg.disable();
        return fg;
      });
      return this._fb.array(formGroups);
    };

    const buildGasConsumptionFormArray = (
      data: NaturalGasMeterConsumption[] | null
    ): FormArray<FormGroup<ConsumptionsFormGroup>> => {
      const formGroups = (data ?? []).map((consumption) => {
        const fg = this._fb.group<ConsumptionsFormGroup>({
          date: this._fb.control(consumption.date, { nonNullable: true }),
          offPeak: this._fb.control(0, { nonNullable: true }),
          peak: this._fb.control(0, { nonNullable: true }),
          standard: this._fb.control(0, { nonNullable: true }),
          superOffPeak: this._fb.control(0, { nonNullable: true }),
          gas: this._fb.control(consumption.consumption, { nonNullable: true }),
          id: this._fb.control(consumption.id, { nonNullable: true }),
          singleLine: this._fb.control(false, { nonNullable: true }),
        });
        fg.disable();
        return fg;
      });
      return this._fb.array(formGroups);
    };

    if (type === 'electricity') {
      return this._invoiceProcessorData.getElectricityMeterByCode(code, this.apiUrl).pipe(
        switchMap((res) => {
          const meter = res.data;
          if (!meter?.id)
            return of({
              meter: null,
              consumptions: this._fb.array<FormGroup<ConsumptionsFormGroup>>(
                []
              ),
            });
          return this._invoiceProcessorData
            .getElectricityMeterConsumptions(meter.id, from, to, this.apiUrl)
            .pipe(
              map((c) => ({
                meter,
                consumptions: buildElectricityConsumptionFormArray(c.data),
              }))
            );
        }),
        catchError((err) =>
          err.status === 404
            ? of({
              meter: null,
              consumptions: this._fb.array<FormGroup<ConsumptionsFormGroup>>(
                []
              ),
            })
            : throwError(() => err)
        )
      );
    }

    // gas
    return this._invoiceProcessorData.getNaturalGasMeterByCode(code, this.apiUrl).pipe(
      switchMap((res) => {
        const meter = res.data;
        if (!meter?.id)
          return of({
            meter: null,
            consumptions: this._fb.array<FormGroup<ConsumptionsFormGroup>>([]),
          });
        return this._invoiceProcessorData
          .getNaturalGasMeterConsumptions(meter.id, from, to, this.apiUrl)
          .pipe(
            map((c) => ({
              meter,
              consumptions: buildGasConsumptionFormArray(c.data),
            }))
          );
      }),
      catchError((err) =>
        err.status === 404
          ? of({
            meter: null,
            consumptions: this._fb.array<FormGroup<ConsumptionsFormGroup>>(
              []
            ),
          })
          : throwError(() => err)
      )
    );
  }

  /* ------------------------- BUILD FORM GROUPS---------------------------- */
  private async mapAzureResponse(
    resp: any,
    file: File
  ): Promise<InvoiceDataFormGroup> {
    this._spinner.hide();

    const d = resp?.data?.analyzeResult?.documents?.[0]?.fields ?? {};

    const gs = (k: string) => d[k]?.valueString?.trim?.() ?? null;
    const gd = (k: string) => d[k]?.valueDate ? new Date(d[k].valueDate) : null;
    const gn = (k: string) => this.parseNumber(d[k]?.valueString);

    const hasAny = (...keys: string[]) => keys.some((key) => !!gs(key));

    const hasElectricity = hasAny(
      'CPEEquipmentCode',
      'ElectricityContractedPower',
      'ElectricityCountType',
      'ElectricityTensionLevel',
      'ElectricityTimeCicle',
      'ElectricityTimeCicle',
      'ElectricityPriceP1',
      'ElectricityPriceP2',
      'ElectricityPriceP3',
      'ElectricityPriceP4',
      'ConsumptionP1',
      'ConsumptionP2',
      'ConsumptionP3',
      'ConsumptionP4',
      'ElectricityTotalConsumption'
    );

    const hasNaturalGas = hasAny(
      'CUIEquipmentCode',
      'GasPressureLevel',
      'GasRating',
      'GasPrice',
      'GasTotalConsumption'
    );

    const endDate = new Date();
    const startDate = subMonths(startOfMonth(endDate), 11);

    const rawTin = gs('CustomerTaxId')?.trim()?.replace(/\s+/g, '');
    const tin = rawTin?.replace(/[^\d]/g, ''); // Mantém apenas números

    const electricityCode = gs('CPEEquipmentCode')?.trim()?.replace(/\s+/g, '');
    const naturalGasCode = gs('CUIEquipmentCode')?.trim()?.replace(/\s+/g, '');

    let formGroup = {
      isElectricityInvoice: new FormControl(hasElectricity),
      isNaturalGasInvoice: new FormControl(hasNaturalGas),
      isPrimaryInvoice: new FormControl(false),
      file: new FormControl<File | null>(file),

      supplierName: new FormControl(gs('SupplierName')),
      supplierBrandLogo: new FormControl<string | null>(null),
      supplierId: new FormControl<number | null>(null),

      supplyAddress: new FormControl(gs('EquipmentSupplyAddress')),
      supplyParish: new FormControl(gs('EquipmentSupplyParish')),
      supplyZipCode: new FormControl(gs('EquipmentSupplyPostalCode')),

      invoiceDueDate: new FormControl(gd('InvoiceDueDate')),
      invoiceServiceStartDate: new FormControl(gd('ServiceStartDate')),
      invoiceServiceEndDate: new FormControl(gd('ServiceEndDate')),
      invoiceTotal: new FormControl(gn('InvoiceTotal')),
      invoicePaymentTerm: new FormControl(gs('PaymentTerm')),
      invoiceIBAN: new FormControl(gs('IBAN')),

      customerTin: new FormControl(tin),
      customerEmail: new FormControl(gs('CustomerEmail')),
      customerName: new FormControl(gs('CustomerName')),
      customerPhoneNumber: new FormControl(gs('CustomerPhoneNumber')),
      customerParish: new FormControl(gs('CustomerParish')),
      customerPostalCode: new FormControl(gs('CustomerPostalCode')),
      customerAddress: new FormControl(gs('CustomerAddress')),

      /* --------------------------- Electricity -------------------------- */
      electricityEquipmentCode: new FormControl(electricityCode),
      electricityContractedPower: new FormControl(
        gn('ElectricityContractedPower')
      ),
      electricityContractedPowerPrice: new FormControl(
        gn('ContractedPowerPrice')
      ),
      electricityContractedPowerPHPPrice: new FormControl(
        gn('ElectricityContractedPowerPHPPrice')
      ),

      electricityCountType: new FormControl(gs('ElectricityCountType')),
      electricityCountTypeId: new FormControl(null),

      electricityTensionLevel: new FormControl(gs('ElectricityTensionLevel')),
      electricityTensionLevelId: new FormControl(null),

      electricityTimeCycle: new FormControl(gs('ElectricityTimeCicle')),
      electricityTimeCycleId: new FormControl(null),

      electricityConsumptionP1: new FormControl(gn('ConsumptionP1')),
      electricityConsumptionP2: new FormControl(gn('ConsumptionP2')),
      electricityConsumptionP3: new FormControl(gn('ConsumptionP3')),
      electricityConsumptionP4: new FormControl(gn('ConsumptionP4')),
      electricityPriceP1: new FormControl(gn('ElectricityPriceP1')),
      electricityPriceP2: new FormControl(gn('ElectricityPriceP2')),
      electricityPriceP3: new FormControl(gn('ElectricityPriceP3')),
      electricityPriceP4: new FormControl(gn('ElectricityPriceP4')),
      electricityTotalConsumption: new FormControl(
        gn('ElectricityTotalConsumption')
      ),

      /* ----------------------------- Gas -------------------------------- */
      naturalGasEquipmentCode: new FormControl(naturalGasCode),

      naturalGasPressureLevel: new FormControl(gs('GasPressureLevel')),
      naturalGasPressureLevelId: new FormControl(null),

      naturalGasRating: new FormControl(gs('GasRating')),
      naturalGasRatingId: new FormControl(null),

      naturalGasTier: new FormControl(gs('GasTier')),
      naturalGasTierId: new FormControl(null),

      naturalGasPrice: new FormControl(gn('GasPrice')),
      naturalGasTotalConsumption: new FormControl(gn('GasTotalConsumption')),

      raw: new FormControl(resp),
    };

    if (!formGroup.electricityEquipmentCode.value) {
      formGroup.electricityEquipmentCode.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((code) => {
          this.getMeterWithConsumptions(
            'electricity',
            code,
            startDate,
            endDate
          );
        });
    }

    if (!String.isNullOrUndefinedOrWhitespace(formGroup.supplierName.value)) {
      try {
        const supplier = await this.getSuppliers(formGroup.supplierName.value);
        formGroup.supplierBrandLogo.setValue(supplier.brandLogo);
        formGroup.supplierId.setValue(supplier.id);
        formGroup.supplierName.setValue(supplier.name);
      } catch { }
    }

    return formGroup;
  }

  private buildGroup<T, K = any>(
    iv: T | null,
    sv: T | null,
    iid?: K | null,
    sid?: K | null,
    validators?: FormControlOptions | ValidatorFn | ValidatorFn[]
  ): FormGroup<DataComparisonFormGroup<T, K>> {
    const idMatch = iid !== null && sid !== null && iid === sid;
    const valueMatch = iv !== null && sv !== null && iv === sv;
    const notFound = iv === null;

    const comparison: 'match' | 'mismatch' | 'notFound' = notFound
      ? 'notFound'
      : idMatch || valueMatch
        ? 'match'
        : 'mismatch';

    const normalizedOptions: FormControlOptions =
      this.normalizeControlOptions(validators);

    const builtGroup = new FormGroup<DataComparisonFormGroup<T, K>>({
      invoiceValue: new FormControl<NonNullable<T> | null>(
        iv ?? null,
        normalizedOptions
      ),
      serverValue: new FormControl<NonNullable<T> | null>(
        sv ?? null,
        normalizedOptions
      ),
      finalValue: new FormControl<NonNullable<T> | null>(
        sv ?? iv ?? null,
        normalizedOptions
      ),
      invoiceId: new FormControl<K | null>(iid ?? null),
      serverId: new FormControl<K | null>(sid ?? null),
      finalId: new FormControl<K | null>(sid ?? iid ?? null),
      comparisonResult: new FormControl<'match' | 'mismatch' | 'notFound'>(
        comparison
      ),
    });

    //If data exists in db -> disable to prevent data changes
    //TODO make the disable optional
    if (builtGroup.controls.serverValue.value) {
      builtGroup.controls.serverValue.disable();
      builtGroup.controls.finalValue.disable();
    }

    if (builtGroup.controls.serverId?.value) {
      builtGroup.controls.serverId.disable();
      builtGroup.controls.finalId?.disable();
    }

    return builtGroup;
  }

  private compose(
    type: EnergyType,
    p: InvoiceDataFormGroup | null,
    sd: any | null,
    consumptions: FormArray<FormGroup<ConsumptionsFormGroup>>
  ): EnergyMeter {
    if (type === 'electricity') {
      return this.composeElectricity(p, sd, consumptions);
    }
    return this.composeGas(p, sd, consumptions);
  }

  private composeElectricity(
    p: InvoiceDataFormGroup | null,
    sd: any | null,
    consumptions: FormArray<FormGroup<ConsumptionsFormGroup>>
  ): FormGroup<ElectricityEquipmentFormGroup> {
    let formGroup = new FormGroup<ElectricityEquipmentFormGroup>({
      equipmentCode: this.buildGroup(
        p?.electricityEquipmentCode.value,
        sd?.code,
        null,
        sd?.equipmentId,
        [
          Validators.required,
          Validators.pattern(this._meterCodeRegexpPattern(true)),
        ]
      ),
      contractedPower: this.buildGroup(
        p?.electricityContractedPower.value,
        sd?.contractedPower,
        null,
        sd?.contractedPowerId,
        { nonNullable: true, validators: Validators.required }
      ),
      tensionLevel: this.buildGroup(
        p?.electricityTensionLevel.value,
        sd?.tensionLevel,
        p?.electricityTensionLevelId.value,
        sd?.tensionLevelId,
        { nonNullable: true, validators: Validators.required }
      ),
      timeCycle: this.buildGroup(
        p?.electricityTimeCycle.value,
        sd?.timeCycle,
        p?.electricityTimeCycleId.value,
        sd?.timeCycleId,
        { nonNullable: true, validators: Validators.required }
      ),
      countType: this.buildGroup(
        p?.electricityCountType.value,
        sd?.countType,
        p?.electricityCountTypeId.value,
        sd?.countTypeId,
        { nonNullable: true, validators: Validators.required }
      ),

      offPeakEnergyPrice: new FormControl(p?.electricityPriceP4.value ?? null),
      peakEnergyPrice: new FormControl(p?.electricityPriceP1.value ?? null),
      standardEnergyPrice: new FormControl(p?.electricityPriceP2.value ?? null),
      superOffPeakEnergyPrice: new FormControl(
        p?.electricityPriceP3.value ?? null
      ),

      county: this.buildGroup(null, sd?.address?.county, {
        nonNullable: true,
        validators: Validators.required,
      }),
      district: this.buildGroup(null, sd?.address?.district, {
        nonNullable: true,
        validators: Validators.required,
      }),
      locality: this.buildGroup(null, sd?.address?.locality, {
        nonNullable: true,
        validators: Validators.required,
      }),
      parish: this.buildGroup(
        p?.supplyParish,
        sd?.address?.parish,
        null,
        sd?.address?.parishId,
        { nonNullable: true, validators: Validators.required }
      ),
      street: this.buildGroup(p?.supplyAddress, sd?.address?.street, {
        nonNullable: true,
      }),
      floor: this.buildGroup(null, sd?.address?.floor, { nonNullable: true }),
      door: this.buildGroup(null, sd?.address?.door, { nonNullable: true }),
      postalCode: this.buildGroup(
        p?.supplyZipCode.value,
        sd?.address?.postalCode,
        null,
        sd?.address?.postalCodeId,
        { nonNullable: true, validators: Validators.required }
      ),

      equipmentId: new FormControl(sd?.id ?? null),
      postalCodeId: new FormControl(sd?.address?.postalCodeId ?? null),
      consumptions: consumptions,
      associatedWithOpportunity: new FormControl(null),
    });

    const tensionLevelControl = formGroup.get(
      'tensionLevel.finalId'
    ) as FormControl<string | null>;
    const countTypeControl = formGroup.get('countType.finalId') as FormControl<
      string | null
    >;
    const timeCycleControl = formGroup.get('timeCycle.finalId') as FormControl<
      string | null
    >;

    const getDescriptionById = (
      map: Map<string, string>,
      id: string
    ): string | null => {
      for (const [description, value] of map.entries()) {
        if (value === id) return description;
      }
      return null;
    };

    //Tension Level Subscription
    this.subscriptions.push(
      tensionLevelControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          map((tensionLevelId) => {
            if (!tensionLevelId) return null;
            const list = this.enums?.tensionLevels ?? new Map();
            return getDescriptionById(list, tensionLevelId);
          })
        )
        .subscribe((description) => {
          formGroup.controls.tensionLevel.controls.finalValue.setValue(
            description ? this._translateService.instant(description) : null
          );
        })
    );
    //Count Type Subscription
    this.subscriptions.push(
      countTypeControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          map((countTypeId) => {
            if (!countTypeId) return null;
            const list = this.enums?.countTypes ?? new Map();
            return getDescriptionById(list, countTypeId);
          })
        )
        .subscribe((description) => {
          formGroup.controls.countType.controls.finalValue.setValue(
            description ? this._translateService.instant(description) : null
          );
        })
    );
    //Time Cycle Subscription
    this.subscriptions.push(
      timeCycleControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          map((timeCycleId) => {
            if (!timeCycleId) return null;
            const list = this.enums?.timeCycles ?? new Map();
            return getDescriptionById(list, timeCycleId);
          })
        )
        .subscribe((description) => {
          formGroup.controls.timeCycle.controls.finalValue.setValue(
            description ? this._translateService.instant(description) : null
          );
        })
    );

    if (p) formGroup.addControl('invoiceData', new FormGroup(p));

    return formGroup;
  }

  private composeGas(
    p: InvoiceDataFormGroup | null,
    sd: any | null,
    consumptions: FormArray<FormGroup<ConsumptionsFormGroup>>
  ): FormGroup<NaturalGasEquipmentFormGroup> {
    let formGroup = new FormGroup<NaturalGasEquipmentFormGroup>({
      equipmentCode: this.buildGroup(
        p?.naturalGasEquipmentCode.value,
        sd?.code,
        null,
        sd?.equipmentId,
        [
          Validators.required,
          Validators.pattern(this._meterCodeRegexpPattern(false)),
        ]
      ),
      pressureLevel: this.buildGroup(
        p?.naturalGasPressureLevel.value,
        sd?.pressureLevel,
        p?.naturalGasPressureLevelId.value,
        sd?.pressureLevelId,
        { nonNullable: true, validators: Validators.required }
      ),
      tariffOption: this.buildGroup(
        p?.naturalGasRating.value,
        sd?.tariffOption,
        p?.naturalGasRatingId.value,
        sd?.tariffOptionId,
        { nonNullable: true }
      ),
      tier: this.buildGroup(
        p?.naturalGasTier.value,
        sd?.tier,
        p?.naturalGasTierId.value,
        sd?.tierId,
        { nonNullable: true, validators: Validators.required }
      ),

      county: this.buildGroup(null, sd?.address?.county, { nonNullable: true }),
      district: this.buildGroup(null, sd?.address?.district, {
        nonNullable: true,
      }),
      locality: this.buildGroup(null, sd?.address?.locality, {
        nonNullable: true,
      }),
      parish: this.buildGroup(
        null,
        sd?.address?.parish,
        null,
        sd?.address?.parishId,
        { nonNullable: true, validators: Validators.required }
      ),
      street: this.buildGroup(null, sd?.address?.street, { nonNullable: true }),
      floor: this.buildGroup(null, sd?.address?.floor, { nonNullable: true }),
      door: this.buildGroup(null, sd?.address?.door, { nonNullable: true }),
      postalCode: this.buildGroup(
        p?.supplyZipCode.value,
        sd?.postalCode,
        null,
        sd?.postalCodeId,
        { nonNullable: true, validators: Validators.required }
      ),

      energyPrice: this.buildGroup(p?.naturalGasPrice.value, sd?.energyPrice),

      equipmentId: new FormControl(sd?.id ?? null),
      postalCodeId: new FormControl(sd?.address?.postalCodeId ?? null),
      consumptions: consumptions,

      associatedWithOpportunity: new FormControl(false, { nonNullable: true }),
    });

    if (p) formGroup.addControl('invoiceData', new FormGroup(p));

    return formGroup;
  }

  /* ------------------------- AUX FUNCTIONS -------------------------- */
  private normalizeControlOptions(
    options?: FormControlOptions | ValidatorFn | ValidatorFn[]
  ): FormControlOptions {
    if (!options) return {};

    if (Array.isArray(options)) {
      return { validators: options };
    }

    if (typeof options === 'function') {
      return { validators: [options] };
    }

    return options;
  }

  /**
   * Get the supplier by name provided by the invoice.
   * @param supplierName Supplier name to search
   * @returns {Promise<Supplier>} Supplier object
   */
  private async getSuppliers(supplierName: string): Promise<Supplier> {
    const sr = await firstValueFrom(
      this._invoiceProcessorData.getSuppliers(supplierName, undefined, true)
    );
    return sr.data[0];
  }

  private parseNumber(numStr?: string): number | null {
    if (!numStr) return null;
    return (
      Number(
        numStr
          .replace(/[^0-9,.-]/g, '')
          .replace(/\./g, '')
          .replace(',', '.')
      ) || null
    );
  }

  private _meterCodeRegexpPattern(isElectricity: boolean): RegExp {
    if (isElectricity) {
      if (this.countryCode.toUpperCase() == 'PT') {
        return new RegExp(RegExpPatterns.elecEquipmentPT);
      } else {
        return new RegExp(RegExpPatterns.elecEquipmentES);
      }
    } else {
      if (this.countryCode.toUpperCase() == 'PT') {
        return new RegExp(RegExpPatterns.ntrlGasEquipmentPT);
      } else {
        return new RegExp(RegExpPatterns.ntrlGasEquipmentES);
      }
    }
  }

  private resolveIds(
    p: InvoiceDataFormGroup
  ): Observable<InvoiceDataFormGroup> {
    return this.enums$.pipe(
      map((e) => {
        p.electricityCountTypeId = this.getIdFromAliases(
          p.electricityCountType.value,
          countTypeKeywords,
          e.countTypes
        );
        p.electricityTimeCycleId = this.getIdFromAliases(
          p.electricityTimeCycle.value,
          timeCycleKeywords,
          e.timeCycles
        );
        p.electricityTensionLevelId = this.getIdFromAliases(
          p.electricityTensionLevel.value,
          tensionLevelAliases,
          e.tensionLevels
        );
        p.naturalGasPressureLevelId = this.getIdFromAliases(
          p.naturalGasPressureLevel.value,
          pressureLevelAliases,
          e.pressureLevels
        );
        p.naturalGasRatingId = this.getIdFromAliases(
          p.naturalGasRating.value,
          tariffOptionKeywords,
          e.tariffOptions
        );
        p.naturalGasTierId = this.getIdFromAliases(
          p.naturalGasTier.value,
          tierKeywords,
          e.tiers
        );
        return p;
      })
    );
  }

  private getIdFromAliases(
    desc: string | null,
    aliasList: { value: string; keywords: string[]; }[],
    enumMap: Map<string, any>
  ): FormControl<string | number | null> {
    if (!desc) return new FormControl(null);
    const normalized = this.normalizeString(desc);

    for (const entry of aliasList) {
      for (const keyword of entry.keywords) {
        if (normalized.includes(this.normalizeString(keyword))) {
          const enumId = enumMap.get(entry.value);
          if (enumId) return new FormControl(enumId);
        }
      }
    }

    return new FormControl(null);
  }

  private normalizeString(input: string): string {
    return input
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove accents
      .replace(/[^a-z0-9 ]/g, ''); // remove punctuation
  }

  private deepUnwrapForm(form: FormGroup | FormArray | FormControl | any): any {
    if (form instanceof FormControl) {
      return form.value;
    }

    if (form instanceof FormArray) {
      return form.controls.map((ctrl) => this.deepUnwrapForm(ctrl));
    }

    if (form instanceof FormGroup) {
      const raw = form.getRawValue();
      return this.deepUnwrapForm(raw);
    }

    if (Array.isArray(form)) {
      return form.map((item) => this.deepUnwrapForm(item));
    }

    if (form && typeof form === 'object') {
      const result: any = {};
      for (const key of Object.keys(form)) {
        result[key] = this.deepUnwrapForm(form[key]);
      }
      return result;
    }

    return form;
  }
}
